import { useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Check, FileAudio, Mic, Radio, Square, Upload, Volume2 } from 'lucide-react';
import {
  createBettieVoiceClone,
  createBettieVoiceMessage,
  createBettieVoiceTts,
  type CreateBettieVoiceCloneResponse,
  type CreateBettieVoiceMessageResponse,
  type CreateBettieVoiceTtsResponse
} from '@pocapan/shared/domains/bettie/services/bettieService';
import type { components } from '@pocapan/shared/types/schema';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import { useWarningToast } from '../../hooks/useWarningToast';
import { ReactComponent as LikeIcon } from '../../../icons/likeGradientShape.svg';

type BettieChatRoom = components['schemas']['ChatRoom'];
type RecordingTarget = 'clone' | 'message';

interface BettieVoiceCallProps {
  chatRoom: BettieChatRoom;
  chatRoomId: string;
  freeChatCount: number;
  forceVoiceSetup?: boolean;
  hasMoreFreeChat: boolean;
  onOpenCharge: () => void;
  onVoiceReady: () => void;
  onVoiceMessageCreated: (response: CreateBettieVoiceMessageResponse) => void;
  setFreeChatCount: Dispatch<SetStateAction<number>>;
}

interface AudioSource {
  blob: Blob;
  fileName: string;
  objectUrl: string;
}
type AudioUpload = Pick<AudioSource, 'blob' | 'fileName'>;

const WAVEFORM_BARS = [16, 28, 40, 24, 52, 32, 44, 22, 36, 48, 26, 38];
const DEFAULT_TONE_PRESET = 'balanced_korean_call';

const getSupportedAudioMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';

  const supportedTypes = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/aac',
    'audio/ogg;codecs=opus'
  ];

  return supportedTypes.find((type) => MediaRecorder.isTypeSupported(type)) ?? '';
};

const extensionByMimeType = (mimeType: string) => {
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('aac')) return 'aac';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('wav')) return 'wav';
  return 'webm';
};

const createAudioSource = (blob: Blob, fileName: string): AudioSource => ({
  blob,
  fileName,
  objectUrl: URL.createObjectURL(blob)
});

const AUDIO_URL_KEYS = [
  'mediaUrl',
  'media_url',
  'audioUrl',
  'audio_url',
  'fileUrl',
  'file_url',
  'downloadUrl',
  'download_url',
  'url'
];
const AUDIO_CONTAINER_KEYS = ['audio', 'media', 'attachment', 'file', 'tts', 'voiceTts'];

const getMessageAudioUrl = (message: unknown): string | undefined => {
  if (!message || typeof message !== 'object') return undefined;

  const record = message as Record<string, unknown>;
  const directUrl = AUDIO_URL_KEYS.map((key) => record[key]).find(
    (value): value is string => typeof value === 'string' && value.length > 0
  );

  if (directUrl) return directUrl;

  for (const key of AUDIO_CONTAINER_KEYS) {
    const nested = getMessageAudioUrl(record[key]);
    if (nested) return nested;
  }

  return undefined;
};

const getBettieResponseAudioUrl = (
  response: CreateBettieVoiceMessageResponse
): string | undefined => {
  return (
    getMessageAudioUrl(response.bettieMessage) ??
    getMessageAudioUrl(response.message) ??
    getMessageAudioUrl(response.meta)
  );
};

const BettieVoiceCall = ({
  chatRoom,
  chatRoomId,
  freeChatCount,
  forceVoiceSetup = false,
  hasMoreFreeChat,
  onOpenCharge,
  onVoiceReady,
  onVoiceMessageCreated,
  setFreeChatCount
}: BettieVoiceCallProps) => {
  const queryClient = useQueryClient();
  const showWarningToast = useWarningToast();
  const [recordingTarget, setRecordingTarget] = useState<RecordingTarget | null>(null);
  const [cloneAudio, setCloneAudio] = useState<AudioSource | null>(null);
  const [voiceClone, setVoiceClone] = useState<CreateBettieVoiceCloneResponse['voice'] | null>(
    null
  );
  const [sampleTts, setSampleTts] = useState<CreateBettieVoiceTtsResponse['voiceTts'] | null>(null);
  const [requiresSampleConfirmation, setRequiresSampleConfirmation] = useState(
    () => forceVoiceSetup || !chatRoom.hasActiveVoice
  );

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const targetRef = useRef<RecordingTarget | null>(null);
  const previousForceVoiceSetupRef = useRef(forceVoiceSetup);
  const responseAudioRef = useRef<HTMLAudioElement | null>(null);

  const bettieNickname = chatRoom.bettieProfile.nickname ?? 'Bettie';
  const isVoiceReady = !requiresSampleConfirmation;
  const isRecreatingVoice = chatRoom.hasActiveVoice && requiresSampleConfirmation;
  const activeVoicePreviewUrl = useMemo(() => {
    const activeVoice = chatRoom.activeVoice;
    if (!activeVoice || typeof activeVoice !== 'object') return undefined;
    const previewUrl =
      (activeVoice as Record<string, unknown>).providerPreviewUrl ??
      (activeVoice as Record<string, unknown>).previewUrl;
    return typeof previewUrl === 'string' ? previewUrl : undefined;
  }, [chatRoom.activeVoice]);
  const sampleAudioUrl = sampleTts?.audioUrl;
  const isRecording = recordingTarget !== null;

  const playResponseAudio = (audioUrl: string) => {
    responseAudioRef.current?.pause();
    const audio = new Audio(audioUrl);
    responseAudioRef.current = audio;
    audio.play().catch(() => {
      showWarningToast('bottom', '응답 음성을 자동 재생하지 못했어요. 대화방에서 확인해 주세요.');
    });
  };

  const createVoiceCloneMutation = useMutation(
    (audio: AudioSource) =>
      createBettieVoiceClone(chatRoomId, audio.blob, {
        fileName: audio.fileName,
        removeBackgroundNoise: false
      }),
    {
      onSuccess: async (response) => {
        setVoiceClone(response.voice);
        queryClient.invalidateQueries(['chatRoom', chatRoomId]);
        try {
          const tts = await createVoiceTtsMutation.mutateAsync();
          setSampleTts(tts.voiceTts);
        } catch (_error) {
          showWarningToast('bottom', '샘플 음성 생성에 실패했어요. 다시 시도해 주세요.');
        }
      },
      onError: () => {
        showWarningToast('bottom', '목소리 생성에 실패했어요. 다시 시도해 주세요.');
      }
    }
  );

  const createVoiceTtsMutation = useMutation(() =>
    createBettieVoiceTts(chatRoomId, {
      voice_tts: {
        text_type: 'sample',
        tone_preset: DEFAULT_TONE_PRESET
      }
    })
  );

  const createVoiceMessageMutation = useMutation(
    (audio: AudioUpload) =>
      createBettieVoiceMessage(chatRoomId, audio.blob, {
        fileName: audio.fileName,
        tonePreset: DEFAULT_TONE_PRESET,
        llmProvider: 'deepseek'
      }),
    {
      onMutate: () => {
        setFreeChatCount((prev) => Math.max(0, prev - 1));
      },
      onSuccess: (response) => {
        const responseAudioUrl = getBettieResponseAudioUrl(response);
        if (responseAudioUrl) {
          playResponseAudio(responseAudioUrl);
        }
        if (
          response.meta?.bettieChatsCount !== undefined &&
          response.meta.bettieChatsCount !== null
        ) {
          setFreeChatCount((prev) => Math.min(prev, response.meta.bettieChatsCount as number));
        }
        queryClient.invalidateQueries(['ChatRoomMessages', chatRoomId]);
        queryClient.invalidateQueries(['chatRoom', chatRoomId]);
        queryClient.invalidateQueries(['bettieBalances']);
        onVoiceMessageCreated(response);
      },
      onError: () => {
        setFreeChatCount((prev) => prev + 1);
        showWarningToast('bottom', '음성 메시지를 보내지 못했어요. 다시 시도해 주세요.');
      }
    }
  );

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state !== 'inactive') {
        mediaRecorderRef.current?.stop();
      }
      responseAudioRef.current?.pause();
      mediaStreamRef.current?.getTracks().forEach((track) => {
        track.stop();
      });
    };
  }, []);

  useEffect(() => {
    return () => {
      if (cloneAudio?.objectUrl) URL.revokeObjectURL(cloneAudio.objectUrl);
    };
  }, [cloneAudio?.objectUrl]);

  useEffect(() => {
    if (forceVoiceSetup && !previousForceVoiceSetupRef.current) {
      setRequiresSampleConfirmation(true);
      setVoiceClone(null);
      setSampleTts(null);
      setCloneAudio((prev) => {
        if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
        return null;
      });
    }

    previousForceVoiceSetupRef.current = forceVoiceSetup;
  }, [forceVoiceSetup]);

  const startRecording = async (target: RecordingTarget) => {
    if (isRecording) return;

    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      showWarningToast('bottom', '이 브라우저에서는 음성 녹음을 사용할 수 없어요.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedAudioMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      chunksRef.current = [];
      targetRef.current = target;
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        const currentTarget = targetRef.current;
        const type = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        const fileName = `bettie-${currentTarget ?? 'voice'}-${Date.now()}.${extensionByMimeType(type)}`;

        if (currentTarget === 'clone') {
          setCloneAudio((prev) => {
            if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
            return createAudioSource(blob, fileName);
          });
        }
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        chunksRef.current = [];
        targetRef.current = null;
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
        setRecordingTarget(null);

        if (currentTarget === 'message') {
          if (blob.size === 0) {
            showWarningToast('bottom', '녹음된 음성이 없어요. 다시 시도해 주세요.');
            return;
          }
          createVoiceMessageMutation.mutate({ blob, fileName });
        }
      };

      recorder.start();
      setRecordingTarget(target);
    } catch (_error) {
      showWarningToast('bottom', '마이크 권한을 확인해 주세요.');
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    recorder.stop();
  };

  const handleFileSelected = (target: RecordingTarget, file?: File) => {
    if (!file || target !== 'clone') return;

    const nextAudio = createAudioSource(file, file.name);
    setCloneAudio((prev) => {
      if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
      return nextAudio;
    });
  };

  const handleCreateVoiceClone = () => {
    if (!cloneAudio) {
      showWarningToast('bottom', '먼저 음성을 녹음하거나 파일을 업로드해 주세요.');
      return;
    }
    createVoiceCloneMutation.mutate(cloneAudio);
  };

  const handleConfirmSample = () => {
    setRequiresSampleConfirmation(false);
    onVoiceReady();
  };

  const handleStartVoiceMessageRecording = () => {
    if (!hasMoreFreeChat) {
      onOpenCharge();
      return;
    }
    startRecording('message');
  };

  if (!isVoiceReady) {
    return (
      <div className="min-h-screen px-[20px] pt-[calc(83px+env(safe-area-inset-top))] pb-[calc(32px+env(safe-area-inset-bottom))] bg-background-light dark:bg-background-dark">
        <section className="flex flex-col items-center">
          <ProfileAvatar
            thumbnailUrl={chatRoom.bettieProfile.pictureUrl}
            widthAndHeight="88px"
            svgWidthAndHeight="56px"
          />
          <div className="mt-[18px] flex items-center gap-[6px] rounded-full bg-background-elevated px-[12px] py-[7px]">
            <Upload size={14} className="text-primary-light dark:text-primary-dark" />
            <p className="text-[12px] font-semibold leading-[15px] text-text-main-lv2">
              {isRecreatingVoice ? '목소리 다시 만들기' : '목소리 등록 필요'}
            </p>
          </div>
          <h2 className="mt-[14px] text-center text-[24px] font-bold leading-[29px] text-text-main-lv1">
            {isRecreatingVoice ? '새 목소리 준비' : '음성 프로필 준비'}
          </h2>
          <p className="mt-[8px] text-center text-[14px] font-medium leading-[19px] text-text-main-lv4">
            {bettieNickname}의 목소리를 만들고
            <br />
            목소리를 확인하면 음성대화를 시작할 수 있어요.
          </p>
        </section>

        <section className="mt-[28px] grid grid-cols-3 gap-[8px]">
          <VoiceSetupStepCard active={true} completed={Boolean(voiceClone)} label="음성 첨부" />
          <VoiceSetupStepCard
            active={Boolean(voiceClone)}
            completed={Boolean(sampleTts)}
            label="목소리 생성"
          />
          <VoiceSetupStepCard active={Boolean(sampleTts)} completed={false} label="목소리 확인" />
        </section>

        <section className="mt-[20px] rounded-[20px] bg-background-elevated px-[18px] py-[20px] shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_12px_0_rgba(0,0,0,0.08)]">
          <div className="flex items-start gap-[12px]">
            <div className="flex h-[44px] w-[44px] items-center justify-center rounded-full bg-surface-lv2">
              <Mic size={22} className="text-primary-light dark:text-primary-dark" />
            </div>
            <div className="flex-1">
              <p className="text-[17px] font-bold leading-[20px] text-text-main-lv1">
                참조 음성 첨부
              </p>
              <p className="mt-[5px] text-[13px] font-medium leading-[17px] text-text-main-lv4">
                조용한 곳에서 10초 이상 또렷하게 녹음해 주세요.
              </p>
            </div>
          </div>

          <div className="mt-[18px]">
            {cloneAudio ? (
              <AudioPreview audio={cloneAudio} label="첨부한 음성" />
            ) : (
              <div className="flex h-[58px] items-center justify-center rounded-[16px] border border-dashed border-separator2 text-[13px] font-medium text-text-main-lv4">
                녹음하거나 파일을 업로드해 주세요
              </div>
            )}
          </div>

          <div className="mt-[18px] flex items-center gap-[8px]">
            <RecordButton
              isRecording={recordingTarget === 'clone'}
              disabled={isRecording && recordingTarget !== 'clone'}
              onStart={() => startRecording('clone')}
              onStop={stopRecording}
            />
            <UploadButton target="clone" onFileSelected={handleFileSelected} />
          </div>

          <button
            type="button"
            data-no-swipe="true"
            disabled={
              !cloneAudio || createVoiceCloneMutation.isLoading || createVoiceTtsMutation.isLoading
            }
            onClick={handleCreateVoiceClone}
            className="mt-[12px] flex h-[50px] w-full items-center justify-center gap-[6px] rounded-full bg-primary-light text-[14px] font-semibold leading-[17px] text-on-primary disabled:bg-disable disabled:text-on-disable dark:bg-primary-dark"
          >
            {createVoiceCloneMutation.isLoading || createVoiceTtsMutation.isLoading
              ? '목소리 생성 중'
              : isRecreatingVoice
                ? '목소리 다시 만들기'
                : '목소리 만들기'}
          </button>
        </section>

        {(sampleTts || voiceClone) && (
          <section className="mt-[14px] rounded-[20px] bg-background-elevated px-[18px] py-[20px] shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_12px_0_rgba(0,0,0,0.08)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[17px] font-bold leading-[20px] text-text-main-lv1">
                  {bettieNickname} 목소리 확인
                </p>
                <p className="mt-[5px] text-[13px] font-medium leading-[17px] text-text-main-lv4">
                  생성된 목소리를 들어본 뒤 시작해 주세요.
                </p>
              </div>
              <Volume2 size={22} className="text-primary-light dark:text-primary-dark" />
            </div>
            {sampleAudioUrl ? (
              <audio
                data-no-swipe="true"
                className="mt-[16px] w-full"
                controls
                src={sampleAudioUrl}
              >
                <track kind="captions" />
              </audio>
            ) : (
              <Waveform active={createVoiceTtsMutation.isLoading} className="mt-[18px]" />
            )}
            <button
              type="button"
              data-no-swipe="true"
              disabled={!sampleAudioUrl}
              onClick={handleConfirmSample}
              className="mt-[18px] flex h-[50px] w-full items-center justify-center gap-[6px] rounded-full bg-primary-light text-[14px] font-semibold leading-[17px] text-on-primary disabled:bg-disable disabled:text-on-disable dark:bg-primary-dark"
            >
              <Check size={16} />
              확인하고 음성대화 시작
            </button>
          </section>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen px-[20px] pt-[calc(83px+env(safe-area-inset-top))] pb-[calc(32px+env(safe-area-inset-bottom))] bg-background-light dark:bg-background-dark">
      <section className="flex flex-col items-center">
        <div className="relative">
          <div className="absolute inset-[-8px] rounded-full bg-gradient-multi-rainbox-lv2 opacity-80 blur-[8px]" />
          <div className="relative z-[1]">
            <ProfileAvatar
              thumbnailUrl={chatRoom.bettieProfile.pictureUrl}
              widthAndHeight="96px"
              svgWidthAndHeight="60px"
            />
          </div>
        </div>
        <div className="mt-[18px] flex items-center gap-[6px] rounded-full bg-background-elevated px-[12px] py-[7px] shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.06)]">
          <Radio size={14} className="text-primary-light dark:text-primary-dark" />
          <p className="text-[12px] font-semibold leading-[15px] text-text-main-lv2">
            음성대화 가능
          </p>
        </div>
        <h2 className="mt-[14px] text-center text-[24px] font-bold leading-[29px] text-text-main-lv1">
          {bettieNickname} 음성대화
        </h2>
      </section>

      <section className="mt-[28px] rounded-[20px] bg-background-elevated px-[18px] py-[20px] shadow-[0_0_3px_0_rgba(0,0,0,0.05),_0_0_12px_0_rgba(0,0,0,0.08)]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[17px] font-bold leading-[20px] text-text-main-lv1">
              내 음성 보내기
            </p>
            <p className="mt-[5px] text-[13px] font-medium leading-[17px] text-text-main-lv4">
              마이크를 켜고 말하면 텍스트와 답장 음성을 받을 수 있어요.
            </p>
          </div>
          <div className="flex items-center gap-[4px]">
            <LikeIcon width={18} height={18} />
            <span className="text-[14px] font-bold text-text-main-lv1">{freeChatCount}</span>
          </div>
        </div>

        <WalkieTalkieRecorder
          disabled={isRecording && recordingTarget !== 'message'}
          isRecording={recordingTarget === 'message'}
          isWaitingForResponse={createVoiceMessageMutation.isLoading}
          onStart={handleStartVoiceMessageRecording}
          onStop={stopRecording}
        />

        {!hasMoreFreeChat && (
          <button
            type="button"
            data-no-swipe="true"
            onClick={onOpenCharge}
            className="mt-[12px] flex h-[44px] w-full items-center justify-center gap-[5px] rounded-full border border-separator1 text-[13px] font-semibold leading-[16px] text-text-main-lv2"
          >
            <LikeIcon width={16} height={16} />
            하트 충전하기
          </button>
        )}
      </section>

      {activeVoicePreviewUrl && (
        <section className="mt-[14px] rounded-[18px] bg-background-elevated px-[18px] py-[16px]">
          <p className="text-[14px] font-bold leading-[17px] text-text-main-lv1">
            현재 목소리 프리뷰
          </p>
          <audio
            data-no-swipe="true"
            className="mt-[12px] w-full"
            controls
            src={activeVoicePreviewUrl}
          >
            <track kind="captions" />
          </audio>
        </section>
      )}
    </div>
  );
};

export default BettieVoiceCall;

const RecordButton = ({
  isRecording,
  disabled,
  onStart,
  onStop
}: {
  isRecording: boolean;
  disabled?: boolean;
  onStart: () => void;
  onStop: () => void;
}) => {
  return (
    <button
      type="button"
      data-no-swipe="true"
      disabled={disabled}
      onClick={isRecording ? onStop : onStart}
      className={`flex h-[50px] flex-1 items-center justify-center gap-[6px] rounded-full border border-separator1 text-[14px] font-semibold leading-[17px] disabled:text-on-disable ${
        isRecording ? 'text-warning' : 'text-text-main-lv2'
      }`}
    >
      {isRecording ? <Square size={16} fill="currentColor" /> : <Mic size={17} />}
      {isRecording ? '녹음 종료' : '녹음'}
    </button>
  );
};

const WalkieTalkieRecorder = ({
  disabled,
  isRecording,
  isWaitingForResponse,
  onStart,
  onStop
}: {
  disabled: boolean;
  isRecording: boolean;
  isWaitingForResponse: boolean;
  onStart: () => void;
  onStop: () => void;
}) => {
  const statusText = isRecording
    ? '말하는 중'
    : isWaitingForResponse
      ? '응답 대기 중'
      : '말하기 대기';
  const isActive = isRecording || isWaitingForResponse;

  return (
    <div className="mt-[22px]">
      <div
        className={`rounded-[24px] px-[18px] pt-[18px] pb-[20px] transition-colors ${
          isActive
            ? 'bg-[#1f1f1f] text-pure-white'
            : 'bg-surface-lv1 text-text-main-lv1 dark:bg-surface-lv2'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-[7px]">
            <Radio
              size={16}
              className={isActive ? 'text-pure-white' : 'text-primary-light dark:text-primary-dark'}
            />
            <p
              className={`text-[13px] font-bold leading-[16px] ${
                isActive ? 'text-pure-white' : 'text-text-main-lv2'
              }`}
            >
              {statusText}
            </p>
          </div>
          <span
            className={`h-[8px] w-[8px] rounded-full ${
              isRecording
                ? 'bg-warning'
                : isWaitingForResponse
                  ? 'bg-primary-light'
                  : 'bg-text-main-lv4'
            }`}
          />
        </div>

        <div className="mt-[16px] flex flex-col items-center">
          <button
            type="button"
            data-no-swipe="true"
            disabled={disabled || isWaitingForResponse}
            onClick={isRecording ? onStop : onStart}
            className={`flex h-[118px] w-[118px] items-center justify-center rounded-full border-[6px] transition-transform active:scale-[0.98] disabled:bg-disable disabled:text-on-disable ${
              isRecording
                ? 'border-[rgba(255,255,255,0.18)] bg-warning text-pure-white'
                : isWaitingForResponse
                  ? 'border-[rgba(255,255,255,0.18)] bg-surface-lv2 text-text-main-lv4'
                  : 'border-[rgba(255,255,255,0.72)] bg-primary-light text-on-primary shadow-[0_8px_22px_rgba(122,89,255,0.24)] dark:bg-primary-dark'
            }`}
            aria-label={isRecording ? '녹음 종료' : '녹음 시작'}
          >
            {isRecording ? (
              <Square size={34} fill="currentColor" />
            ) : isWaitingForResponse ? (
              <Radio size={40} />
            ) : (
              <Mic size={42} />
            )}
          </button>
          <p
            className={`mt-[12px] text-[14px] font-bold leading-[17px] ${
              isActive ? 'text-pure-white' : 'text-text-main-lv1'
            }`}
          >
            {isRecording
              ? '다시 누르면 자동 전송'
              : isWaitingForResponse
                ? '답장을 기다리고 있어요'
                : '눌러서 말하기'}
          </p>
          <Waveform active={isActive} className={isActive ? 'mt-[8px]' : 'mt-[8px] opacity-70'} />
        </div>
      </div>
    </div>
  );
};

const UploadButton = ({
  target,
  onFileSelected
}: {
  target: RecordingTarget;
  onFileSelected: (target: RecordingTarget, file?: File) => void;
}) => {
  return (
    <label
      data-no-swipe="true"
      className="flex h-[50px] flex-1 cursor-pointer items-center justify-center gap-[6px] rounded-full border border-separator1 text-[14px] font-semibold leading-[17px] text-text-main-lv2"
    >
      <FileAudio size={17} />
      파일 업로드
      <input
        data-no-swipe="true"
        className="sr-only"
        type="file"
        accept="audio/*"
        onChange={(event) => {
          onFileSelected(target, event.target.files?.[0]);
          event.target.value = '';
        }}
      />
    </label>
  );
};

const AudioPreview = ({ audio, label }: { audio: AudioSource; label: string }) => {
  return (
    <div className="rounded-[16px] bg-surface-lv1 px-[14px] py-[13px]">
      <div className="mb-[10px] flex items-center justify-between gap-[8px]">
        <p className="min-w-0 truncate text-[13px] font-bold leading-[16px] text-text-main-lv2">
          {label}
        </p>
        <p className="min-w-0 truncate text-[12px] font-medium leading-[15px] text-text-main-lv4">
          {audio.fileName}
        </p>
      </div>
      <audio data-no-swipe="true" className="w-full" controls src={audio.objectUrl}>
        <track kind="captions" />
      </audio>
    </div>
  );
};

const Waveform = ({ active, className = '' }: { active: boolean; className?: string }) => {
  return (
    <div className={`flex h-[58px] items-center justify-center gap-[5px] ${className}`}>
      {WAVEFORM_BARS.map((height) => (
        <span
          key={height}
          className={`w-[5px] rounded-full transition-colors ${
            active ? 'bg-primary-light dark:bg-primary-dark' : 'bg-surface-lv2'
          }`}
          style={{ height }}
        />
      ))}
    </div>
  );
};

const VoiceSetupStepCard = ({
  active,
  completed,
  label
}: {
  active: boolean;
  completed: boolean;
  label: string;
}) => {
  return (
    <div
      className={`flex h-[70px] flex-col items-center justify-center gap-[6px] rounded-[16px] border ${
        active
          ? 'border-primary-light bg-background-elevated dark:border-primary-dark'
          : 'border-separator1 bg-transparent'
      }`}
    >
      <div
        className={`flex h-[22px] w-[22px] items-center justify-center rounded-full ${
          completed || active
            ? 'bg-primary-light text-on-primary dark:bg-primary-dark'
            : 'bg-surface-lv2 text-text-main-lv4'
        }`}
      >
        {completed ? (
          <Check size={13} />
        ) : (
          <span className="h-[7px] w-[7px] rounded-full bg-current" />
        )}
      </div>
      <p className="text-[12px] font-semibold leading-[15px] text-text-main-lv2">{label}</p>
    </div>
  );
};
